import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTokens } from '../hooks/useTokens';
import { useNotes } from '../hooks/useNotes';
import { usePins } from '../hooks/usePins';
import { useCanvasContext } from './CanvasContext';
import { useSessionContext } from './SessionContext';

type UseTokensReturn = ReturnType<typeof useTokens>;
type UseNotesReturn = ReturnType<typeof useNotes>;
type UsePinsReturn = ReturnType<typeof usePins>;

export interface TokenContextValue {
  tokens: UseTokensReturn;
  notes: UseNotesReturn;
  /** @deprecated Use pins */
  pins: UsePinsReturn;
}

const TokenContext = createContext<TokenContextValue | null>(null);

export function TokenProvider({ children }: { children: ReactNode }) {
  const { engine } = useCanvasContext();
  const { mp, sessionSettings } = useSessionContext();
  const tokens = useTokens(engine, { defaultTokenSize: sessionSettings.defaultTokenSize });
  const notes = useNotes(mp);
  const pins = usePins(mp);

  const value = useMemo(() => ({ tokens, notes, pins }), [tokens, notes, pins]);

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokenContext() {
  const ctx = useContext(TokenContext);
  if (!ctx) throw new Error('useTokenContext must be used within TokenProvider');
  return ctx;
}
